import { useCallback, useEffect, useRef, useState } from "react";
import { useAccount, useBalance, usePublicClient, useReadContract } from "wagmi";
import { formatUnits, parseUnits } from "viem";
import { erc20Abi } from "../lib/abis";
import { getQuote, NATIVE } from "../lib/swap";
import { getApiQuote } from "../lib/tradingApi";
import { errMsg } from "../lib/api";

export function useTokenBalance(token) {
  const { address } = useAccount();
  const native = useBalance({ address, chainId: 4663, query: { enabled: !!address && token?.address === NATIVE, refetchInterval: 15000 } });
  const erc = useReadContract({ address: token?.address !== NATIVE ? token?.address : undefined, abi: erc20Abi, functionName: "balanceOf",
    args: [address], chainId: 4663, query: { enabled: !!address && !!token && token.address !== NATIVE, refetchInterval: 15000 } });
  const raw = token?.address === NATIVE ? native.data?.value : erc.data;
  return { raw, formatted: raw !== undefined && token ? formatUnits(raw, token.decimals) : null, refetch: token?.address === NATIVE ? native.refetch : erc.refetch };
}

export function useAllowance(token, spender) {
  const { address } = useAccount();
  const r = useReadContract({ address: token?.address !== NATIVE ? token?.address : undefined, abi: erc20Abi, functionName: "allowance",
    args: [address, spender], chainId: 4663, query: { enabled: !!address && !!token && token.address !== NATIVE } });
  return { allowance: token?.address === NATIVE ? null : r.data, refetch: r.refetch };
}

// Tells apart "no pool/route" from service failures so users never see a fake or misleading quote.
const classifyApiError = (e) => {
  const st = e?.response?.status;
  const m = errMsg(e);
  if (st === 404 || /no quotes|no route/i.test(m)) return "No liquidity: no Uniswap v2/v3/v4 route for this pair at this amount (the pool may be empty, or the amount too large/small for its liquidity).";
  if (st === 429) return "Too many quote requests — wait a few seconds.";
  if (st === 400 || st === 422) return `Quote rejected: ${m}`;
  return `Quote service unavailable (${m})`;
};

export function useQuote({ cfg, tokenIn, tokenOut, amount, slippage, wallet }) {
  const pc = usePublicClient({ chainId: 4663 });
  const [state, setState] = useState({ quote: null, loading: false, error: null });
  const reqId = useRef(0);
  const key = `${tokenIn?.address}|${tokenOut?.address}|${amount}|${slippage}`;

  useEffect(() => { setState({ quote: null, loading: false, error: null }); }, [key]);

  const run = useCallback(async () => {
    const id = ++reqId.current;
    const done = (s) => { if (id === reqId.current) setState(s); };
    let valid = false;
    try { valid = !!amount && parseUnits(amount, tokenIn.decimals) > 0n; } catch { valid = false; }
    if (!valid || !tokenOut) return done({ quote: null, loading: false, error: null });
    setState((s) => ({ ...s, loading: true, error: null }));
    try {
      let apiError = null;
      if (cfg.trading_api_enabled) {
        try {
          const q = await getApiQuote({ tokenIn, tokenOut, amountIn: amount, slippage, wallet });
          if (q) return done({ quote: { ...q, key }, loading: false, error: null });
        } catch (e) { apiError = classifyApiError(e); }
      }
      const feeTokens = cfg.swap_fee?.tokens || [];
      if ((apiError || !cfg.trading_api_enabled) && [tokenIn.address, tokenOut.address].some((a) => feeTokens.includes(String(a).toLowerCase())))
        throw new Error(`Quote unavailable: ${apiError || "Trading API disabled"}. Swaps of HIVEVORA-listed tokens route through the Uniswap Trading API only — try again shortly.`);
      let quote;
      try { quote = await getQuote({ pc, cfg, tokenIn, tokenOut, amountIn: amount }); }
      catch (e) {
        const rpcDown = /fetch|network|timeout|HTTP request failed|429/i.test(e?.shortMessage || e?.message || "");
        throw new Error(rpcDown ? `Robinhood Chain RPC unavailable — couldn't read pools. ${apiError ? `Trading API: ${apiError}` : ""}`
          : apiError?.startsWith("No liquidity") ? apiError : `${e.shortMessage || e.message}${apiError ? ` · Trading API: ${apiError}` : ""}`);
      }
      done({ quote: quote && { ...quote, source: "v3", apiError, key }, loading: false, error: null });
    } catch (e) {
      done({ quote: null, loading: false, error: e.shortMessage || e.message });
    }
  }, [pc, cfg, tokenIn, tokenOut, amount, slippage, wallet, key]);

  useEffect(() => {
    const t = setTimeout(run, 400);
    const iv = setInterval(run, 20000);
    return () => { clearTimeout(t); clearInterval(iv); };
  }, [run]);

  return { ...state, quote: state.quote?.key === key ? state.quote : null, refresh: run };
}
